// ——————————————————
// CONFETTI ANIMATION
// ——————————————————
type Position = {
  x: number
  y: number
}

type Particle = {
  element: SVGRectElement | SVGCircleElement
  startX: number
  startY: number
  velocityX: number
  velocityY: number
  gravity: number
  rotationSpeed: number
}

type SleepingElement = {
  element: SVGTextElement
  startTime: number
  duration: number
  startX: number
  startY: number
}

function createConfetti(insertBeforeEl: Element, pos: Partial<Position> = {}) {
  const { x = 0, y = 0 } = pos
  const count = 50
  const colors = ['#FBAF00', '#FFD639', '#FFA3AF', '#007CBE', '#00AF54']
  const duration = 2000
  const spread = 450

  const particleGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g')
  particleGroup.setAttribute('class', 'confetti-group')
  const parentElement = insertBeforeEl.parentElement
  if (!parentElement) return
  parentElement.insertBefore(particleGroup, insertBeforeEl)

  const particles: Particle[] = []

  for (let i = 0; i < count; i++) {
    const color = colors[Math.floor(Math.random() * colors.length)]
    const particle = createParticle(x, y, color)
    particleGroup.appendChild(particle.element)
    particles.push(particle)
  }

  const startTime = performance.now()

  function animate(currentTime: number) {
    const elapsed = currentTime - startTime
    const progress = Math.min(elapsed / duration, 1)

    particles.forEach(p => {
      const t = progress
      const posX = p.startX + p.velocityX * spread * t
      const posY = p.startY + p.velocityY * spread * t + p.gravity * t * t * 500
      const rotation = p.rotationSpeed * t * 360
      const opacity = 1 - t * t

      p.element.setAttribute('transform', `translate(${posX}, ${posY}) rotate(${rotation})`)
      p.element.setAttribute('opacity', String(opacity))
    })

    if (progress < 1) requestAnimationFrame(animate)
    else particleGroup.remove()
  }

  requestAnimationFrame(animate)
}

function createParticle(x: number, y: number, color: string): Particle {
  const shapes = ['rect', 'circle']
  const shapeType = shapes[Math.floor(Math.random() * shapes.length)]
  const element =
    shapeType === 'rect'
      ? document.createElementNS('http://www.w3.org/2000/svg', 'rect')
      : document.createElementNS('http://www.w3.org/2000/svg', 'circle')
  const size = Math.random() * 6 + 4

  if (shapeType === 'rect') {
    const rectElement = element as SVGRectElement
    rectElement.setAttribute('width', String(size))
    rectElement.setAttribute('height', String(size * (Math.random() * 0.5 + 0.5)))
    rectElement.setAttribute('x', String(-size / 2))
    rectElement.setAttribute('y', String(-size / 2))
  } else {
    const circleElement = element as SVGCircleElement
    circleElement.setAttribute('r', String(size / 2))
  }

  element.setAttribute('fill', color)

  const angle = Math.random() * Math.PI * 2
  const speed = Math.random() * 0.5 + 0.5

  return {
    element,
    startX: x,
    startY: y,
    velocityX: Math.cos(angle) * speed,
    velocityY: Math.sin(angle) * speed - 1,
    gravity: Math.random() * 0.5 + 0.5,
    rotationSpeed: (Math.random() - 0.5) * 4
  }
}

export function confettiBurst(insertBeforeEl: Element, burstCount = 3, pos: Partial<Position> = {}) {
  for (let i = 0; i < burstCount; i++) {
    setTimeout(() => {
      createConfetti(insertBeforeEl, pos)
    }, i * 1500)
  }
}

// ——————————————————
// SLEEPING ANIMATION
// ——————————————————
export function createSleepingAnim(svgContainer: SVGElement, pos: Partial<Position> = {}) {
  const { x = 0, y = 0 } = pos
  const maxCount = 3
  const spawnInterval = 1500
  const duration = 3000
  const color = '#4A90E2'
  const fontSize = 24
  const wobbleAmount = 20
  const riseDistance = 100

  const state = {
    isRunning: true,
    activeElements: [] as SleepingElement[],
    spawnTimer: null as ReturnType<typeof setInterval> | null,
    group: document.createElementNS('http://www.w3.org/2000/svg', 'g')
  }

  svgContainer.appendChild(state.group)

  function spawnLtr() {
    if (!state.isRunning) return
    if (state.activeElements.length >= maxCount) return

    const ltrEl = document.createElementNS('http://www.w3.org/2000/svg', 'text')
    ltrEl.textContent = 'z'
    ltrEl.setAttribute('x', String(x))
    ltrEl.setAttribute('y', String(y))
    ltrEl.setAttribute('font-size', String(fontSize))
    ltrEl.setAttribute('font-family', 'Arial, sans-serif')
    ltrEl.setAttribute('font-weight', 'bold')
    ltrEl.setAttribute('fill', color)
    ltrEl.setAttribute('text-anchor', 'middle')
    ltrEl.setAttribute('dominant-baseline', 'middle')
    ltrEl.setAttribute('stroke', 'white')

    state.group.appendChild(ltrEl)

    const ltrData: SleepingElement = {
      element: ltrEl,
      startTime: performance.now(),
      duration,
      startX: x,
      startY: y
    }

    state.activeElements.push(ltrData)
  }

  function animate(currentTime: number) {
    if (state.activeElements.length === 0 && !state.isRunning) {
      return state.group.remove()
    }

    for (let i = state.activeElements.length - 1; i >= 0; i--) {
      const ltr = state.activeElements[i]
      const elapsed = currentTime - ltr.startTime
      const progress = Math.min(elapsed / ltr.duration, 1)

      if (progress >= 1) {
        ltr.element.remove()
        state.activeElements.splice(i, 1)
        continue
      }

      const wobble = Math.sin(progress * Math.PI * 3) * wobbleAmount * (1 - progress)
      const rise = progress * riseDistance
      const posX = ltr.startX - wobble
      const posY = ltr.startY - rise
      const fadeStart = 0.6
      const opacity = progress < fadeStart ? 1 : 1 - (progress - fadeStart) / (1 - fadeStart)
      const scale = 1 + progress * 0.3

      ltr.element.setAttribute('transform', `translate(${posX}, ${posY}) scale(${scale})`)
      ltr.element.setAttribute('opacity', String(opacity))
    }

    requestAnimationFrame(animate)
  }

  // Start spawning Z's
  function startSpawning() {
    spawnLtr()
    state.spawnTimer = setInterval(() => {
      if (state.isRunning) spawnLtr()
    }, spawnInterval)
  }

  startSpawning()
  requestAnimationFrame(animate)

  return {
    stop: () => {
      state.isRunning = false
      if (state.spawnTimer) {
        clearInterval(state.spawnTimer)
        state.spawnTimer = null
      }
    }
  }
}
